/**
 * shell/sidebar.tsx — admin-shell sidebar for SK Queue.
 * Lists only the admin surfaces visible to (access level ∩ entitlements) — the
 * real-product RBAC filter. Public surfaces (Guest, Now Serving) are NOT here:
 * they're separate QR/display entry points, reachable via the dev launcher in
 * the topbar during the build.
 *
 * Pinned at the FOOT: the account block (avatar + name + org) with a sign-out
 * popover — the app-shell convention of keeping identity/logout at the bottom
 * of the nav. SK Queue owns no profile/settings screen (those live on the
 * platform), so sign-out is the only real action.
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useSurfaceRouter } from '../navigation/surfaceRouter';
import { visibleAdminSurfaces, getSurface } from '../queue/surfaces';
import { Mono, SkMark } from '../queue/components/atoms';

export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { accessLevel, entitlements, org, fullName, user, logout } = useSession();
  const { surface, setSurface } = useSurfaceRouter();
  const [acctOpen, setAcctOpen] = useState(false);

  const items = visibleAdminSurfaces(accessLevel, entitlements).map(getSurface);
  const initials = (fullName || 'SK').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <View style={{ width: 248, backgroundColor: colors.ink, paddingVertical: 16 }}>
      {/* brand — bordered SK mark + "Queue" wordmark ("SK " prefix + org line
          removed per request). */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 18, marginBottom: 18 }}>
        <SkMark size={26} color={colors.white} />
        <Text style={{ fontWeight: '600', fontSize: 15, color: colors.white }}>Queue</Text>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 10, gap: 2 }}>
        {items.map(s => {
          const active = surface === s.key;
          return (
            <TouchableOpacity
              key={s.key}
              onPress={() => { setSurface(s.key); onNavigate?.(); }}
              style={{
                flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                paddingHorizontal: 14, paddingVertical: 11, borderRadius: 9,
                backgroundColor: active ? colors.white : 'transparent',
              }}
            >
              <Text style={{ fontSize: 13.5, fontWeight: '500', color: active ? colors.ink : colors.ink4 }}>{s.label}</Text>
              <Mono style={{ fontSize: 9, letterSpacing: 0.5, color: active ? colors.ink3 : '#5A5A5E' }}>{s.kicker}</Mono>
            </TouchableOpacity>
          );
        })}
        {items.length === 0 && (
          <Text style={{ paddingHorizontal: 14, paddingVertical: 20, fontSize: 12.5, color: colors.ink4 }}>
            No admin surfaces for this role.
          </Text>
        )}
      </ScrollView>

      {/* Account block — pinned to the foot of the sidebar. Tap toggles the
          sign-out popover (positioned above so it never clips off-screen). */}
      <View style={{ paddingHorizontal: 10, paddingTop: 8, position: 'relative' }}>
        {acctOpen && (
          <View style={{
            position: 'absolute', left: 10, right: 10, bottom: 68,
            backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line,
            borderRadius: 12, padding: 6,
            ...(Platform.OS === 'web' ? { boxShadow: '0 12px 30px -10px rgba(0,0,0,.35)', zIndex: 60 } as any : {}),
          }}>
            {!!user?.email && (
              <Mono style={{ fontSize: 9, color: colors.ink4, paddingHorizontal: 10, paddingVertical: 6 }}>
                {user.email}
              </Mono>
            )}
            <TouchableOpacity
              onPress={() => { setAcctOpen(false); onNavigate?.(); logout(); }}
              style={{ paddingHorizontal: 10, paddingVertical: 9, borderRadius: 7 }}
              accessibilityLabel="Sign out"
            >
              <Text style={{ fontSize: 13, fontWeight: '500', color: colors.ink }}>Sign out</Text>
            </TouchableOpacity>
          </View>
        )}

        <TouchableOpacity
          onPress={() => setAcctOpen(o => !o)}
          accessibilityLabel={`Account: ${fullName || 'signed in'}. Tap for sign out.`}
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 10,
            paddingHorizontal: 10, paddingVertical: 10, borderRadius: 10,
            backgroundColor: acctOpen ? 'rgba(255,255,255,0.06)' : 'transparent',
          }}
        >
          <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#2A2A2E', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: colors.white }}>{initials}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: 13, fontWeight: '500', color: colors.white }} numberOfLines={1}>{fullName || 'Signed in'}</Text>
            {!!org?.name && <Mono style={{ fontSize: 9, color: colors.ink4 }}>{org.name}</Mono>}
          </View>
          <Text style={{ fontSize: 10, color: colors.ink4 }}>{acctOpen ? '▾' : '▴'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
